import type { ApiError, AuthSession } from '@facture/contracts'
import { useSession } from './session'
import { sleep } from './utils'

export class ApiRequestError extends Error implements ApiError {
  readonly statusCode: number
  readonly code: string
  readonly details?: Record<string, string[]>

  constructor(error: ApiError) {
    super(error.message)
    this.name = 'ApiRequestError'
    this.statusCode = error.statusCode
    this.code = error.code
    this.details = error.details
  }
}

type Query = Record<string, string | number | boolean | null | undefined>

interface RequestOptions {
  body?: unknown
  query?: Query
  signal?: AbortSignal
  /** No enviar X-Company-Id (rutas globales como /auth o /companies). */
  global?: boolean
  /** Compañía distinta de la activa (el superadmin gestionando otra desde Administración). */
  companyId?: string
  /** Recibe la respuesta cruda (para leer encabezados, p. ej. en descargas). */
  onResponse?: (response: Response) => void
}

const BASE = '/api'

let accessToken: string | null = null
let refreshInFlight: Promise<AuthSession | null> | null = null

function buildUrl(path: string, query?: Query) {
  const url = new URL(BASE + path, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value))
  }
  return url.pathname + url.search
}

async function parseError(response: Response): Promise<ApiRequestError> {
  try {
    const data = (await response.json()) as Partial<ApiError>
    return new ApiRequestError({
      statusCode: response.status,
      code: data.code ?? 'error',
      message: data.message ?? 'Ocurrió un error inesperado',
      details: data.details,
    })
  } catch {
    return new ApiRequestError({
      statusCode: response.status,
      code: response.status >= 500 ? 'server_error' : 'error',
      message: response.status >= 500 ? 'El servidor no respondió correctamente' : 'Solicitud inválida',
    })
  }
}

/** Renueva el access token usando la cookie httpOnly. Una sola renovación en vuelo por pestaña. */
export function refreshSession(): Promise<AuthSession | null> {
  refreshInFlight ??= (async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const response = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
      if (response.ok) {
        const session = (await response.json()) as AuthSession
        accessToken = session.accessToken
        useSession.getState().setSession(session)
        return session
      }
      const error = await parseError(response)
      // Otra pestaña rotó el token un instante antes: la cookie nueva ya está en el navegador.
      if (error.code === 'refresh_superseded') {
        await sleep(250 * (attempt + 1))
        continue
      }
      break
    }
    accessToken = null
    useSession.getState().clear()
    return null
  })().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

export function setAccessToken(token: string | null) {
  accessToken = token
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
  retried = false,
): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' })
  const isForm = options.body instanceof FormData
  if (options.body !== undefined && !isForm) headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  const companyId = options.companyId ?? useSession.getState().companyId
  if (!options.global && companyId) headers.set('X-Company-Id', companyId)

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'include',
      signal: options.signal,
      body:
        options.body === undefined
          ? undefined
          : isForm
            ? (options.body as FormData)
            : JSON.stringify(options.body),
    })
  } catch (cause) {
    if ((cause as Error).name === 'AbortError') throw cause
    throw new ApiRequestError({
      statusCode: 0,
      code: 'network_error',
      message: 'Sin conexión con el servidor',
    })
  }

  if (response.status === 401 && !retried && !path.startsWith('/auth/login')) {
    const session = await refreshSession()
    if (session) return request<T>(method, path, options, true)
  }
  if (!response.ok) {
    const error = await parseError(response)
    // El API también exige el cambio de clave temporal: se muestra la pantalla obligatoria en vez de un error suelto.
    if (response.status === 403 && error.code === 'must_change_password') {
      const { user } = useSession.getState()
      if (user && !user.mustChangePassword)
        useSession.setState({ user: { ...user, mustChangePassword: true } })
    }
    throw error
  }
  options.onResponse?.(response)
  if (response.status === 204) return undefined as T
  const type = response.headers.get('content-type') ?? ''
  return (type.includes('json') ? await response.json() : await response.blob()) as T
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
}

export function errorMessage(error: unknown) {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return 'Ocurrió un error inesperado'
}
